<template>
	<div
		ref="listRef"
		class="relative flex w-full md:w-[calc(33%-28px)] min-h-[56px] pl-0 sm:pl-4 md:pl-6 pr-0 sm:pr-4 mt-3 md:mt-2"
	>
		<div class="flex flex-col flex-1">
			<span class="pb-1 text-dj-gray-dark text-sm font-medium">{{ $t("mainSearchbar.extras.title") }}</span>
			<span v-if="selectedExtras.length" class="block w-full whitespace-nowrap overflow-ellipsis">
				{{ selectedExtras.map((extra) => extra.name[locale]).join(", ") }}
			</span>
			<span v-else class="text-dj-gray">{{ placeholder }}</span>
		</div>

		<DropdownButton v-model="showList" class="mt-2" />

		<div
			v-if="showList"
			class="absolute p-5 left-0 top-[60px] md:top-[85px] w-[240px] max-w-[100%] md:max-w-[100vw] h-[max] bg-white border border-dj-gray-light rounded-big flex flex-col gap-y-[10px] z-10"
		>
			<BaseCheckbox
				v-for="(extra, index) in extras"
				:key="index"
				v-model="extra.value"
				@change="onChangeExtra(extra)"
			>
				{{ extra.name[locale] }}
			</BaseCheckbox>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, type PropType, ref, watch } from "vue";
import { onClickOutside } from "@vueuse/core";
import { useI18n } from "vue-i18n";

type ExtraItem = {
	id: number;
	name: Record<string, string>;
};

type ExtraItemWithValue = ExtraItem & {
	value: boolean;
};

export default defineComponent({
	name: "SearchbarMainExtra",
	props: {
		modelValue: {
			type: Array as PropType<number[]>,
			required: true,
		},
		extrasData: {
			type: Array as PropType<ExtraItem[]>,
			default: () => [],
		},
		placeholder: {
			type: String,
			default: "",
		},
	},
	emits: ["update:modelValue"],
	setup(props, { emit }) {
		const { locale } = useI18n();

		const selectedExtras = ref<ExtraItem[]>([]);
		const extras = ref<ExtraItemWithValue[]>([]);

		const showList = ref(false);
		const listRef = ref(null);

		function addValuePropToExtras(data: ExtraItem[]) {
			extras.value = data.map((extra) => {
				return {
					...extra,
					value: selectedExtras.value.some((selected) => selected.id === extra.id),
				};
			});
		}

		watch(
			() => props.extrasData,
			(value) => {
				addValuePropToExtras(value || []);
			},
			{ immediate: true }
		);

		function onChangeExtra(extra: ExtraItemWithValue) {
			const index = extras.value.findIndex((pred) => pred.name[locale.value] == extra.name[locale.value]);
			if (extras.value[index].value) {
				selectedExtras.value.push(extra);
				emitExtras();
			} else {
				selectedExtras.value = selectedExtras.value.filter(
					(sExtra) => sExtra.name[locale.value] != extra.name[locale.value]
				);
				emitExtras();
			}
		}

		function emitExtras() {
			emit(
				"update:modelValue",
				selectedExtras.value.map((extra) => extra.id)
			);
		}

		onClickOutside(listRef, () => {
			showList.value = false;
		});

		return {
			onChangeExtra,
			selectedExtras,
			showList,
			listRef,
			extras,
			locale,
		};
	},
});
</script>
